import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanGreenhousesAction,
  createGreenhouseAction,
  greenhousesEntitiesSelector,
  greenhousesIdsSelector,
  getGreenhouseListAction,
  isLoadingGreenhousesSelector,
  remoteTotalSelector,
  updateGreenhouseAction,
} from 'state/slices/greenhouse';
import { usePagination } from 'hooks/pagination';
import {
  BenchSmall,
  Greenhouse,
  Organization,
  PaginationData,
} from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateGreenhouseData } from 'api/greenhouse';
import { OrganizationEnum, OrganizationMapping } from 'constants/organization';
import { unwrapResult } from '@reduxjs/toolkit';

import EditModal, { GreenhouseFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';

const GreenhousePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const greenhouseEntities = useSelector(greenhousesEntitiesSelector);
  const greenhouseIds = useSelector(greenhousesIdsSelector);
  const isRemoteLoading = useSelector(isLoadingGreenhousesSelector);
  const [isLoading, setLoading] = useState(false);

  const greenhouses = greenhouseIds.map((greenhouseId) => {
    const entity = greenhouseEntities[greenhouseId];
    if (!entity) return undefined;
    const tableGreenhouse: Greenhouse & {
      key?: string,
    } = {
      ...entity,
      key: greenhouseId.toString(),
    };
    return tableGreenhouse;
  }).filter((tableGreenhouse): tableGreenhouse is Greenhouse => !!tableGreenhouse);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getGreenhouseListAction(paginationData));
  };
  const pagination = usePagination<Greenhouse>(
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanGreenhousesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [greenhouseForm] = Form.useForm<GreenhouseFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditGreenhouse, setActiveEditGreenhouse] = useState<Greenhouse>();

  const openModal = (greenhouseId: string) => {
    const greenhouseEntity = greenhouseEntities[greenhouseId];
    setActiveEditGreenhouse(greenhouseEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditGreenhouse(undefined);
    setEditModalOpen(false);
    greenhouseForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await greenhouseForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: GreenhouseFormData = greenhouseForm.getFieldsValue();

    const greenhouseData: CreateGreenhouseData = {
      name: formData.name,
      organization: formData.organization,
      benches: formData.benches.map((curBench) => ({
        ...curBench,
        capacity: parseInt(curBench.capacity, 10),
      })),
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditGreenhouse) {
        const updateResult = await dispatch(updateGreenhouseAction({
          id: activeEditGreenhouse.id,
          data: {
            ...greenhouseData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createGreenhouseAction(greenhouseData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Estufa ${activeEditGreenhouse ? 'atualizada' : 'criada'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar estufa. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const organizationFilter = pagination.getInternal().filter.organization ?? null;

  return (
    <SectionBasePage extra={<Extra onAddClick={() => openModal('')} />}>
      <>
        <Table
          dataSource={greenhouses}
          loading={isRemoteLoading}
          showSorterTooltip={false}
          pagination={pagination.getInternal()}
          onChange={pagination.onTableChange}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
            sorter
            sortOrder={pagination.getInternal().orderBy === 'name' ? pagination.getInternal().order : undefined}
          />
          <Table.Column
            title="Unidade"
            key="organization"
            dataIndex="organization"
            render={(organization: Organization) => organization.name}
            filters={[{
              text: OrganizationMapping[OrganizationEnum.ORG1].name,
              value: OrganizationEnum.ORG1,
            },
            {
              text: OrganizationMapping[OrganizationEnum.ORG2].name,
              value: OrganizationEnum.ORG2,
            }]}
            filterMultiple={false}
            filteredValue={organizationFilter !== null ? [organizationFilter] : null}
          />
          <Table.Column
            title="Nº de bancas"
            key="benches"
            dataIndex="benches"
            render={(benches: BenchSmall[]) => benches.length}
          />
          <Table.Column
            title="Capacidade"
            key="capacity"
            dataIndex="id"
            render={(greenhouseId: string) => {
              const rowGreenhouse = greenhouseEntities[greenhouseId];
              const capacity = rowGreenhouse
                ?.benches
                .reduce((
                  capacityAccumulator,
                  curBench,
                ) => capacityAccumulator + curBench.capacity, 0);
              return `${capacity} bandejas`;
            }}
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(greenhouseId: string) => (
              <ActionButton onClick={() => openModal(greenhouseId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={greenhouseForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curGreenhouse={activeEditGreenhouse && {
            name: activeEditGreenhouse.name,
            organization: OrganizationEnum.ORG1,
            benches: activeEditGreenhouse.benches,
          }}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default GreenhousePage;
